import { useState } from "react";
import { useForm } from "react-hook-form";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

import { post, patch } from "@/lib/api-client";
import { Modal } from "@/components/ui/Modal";
import { Input } from "@/components/ui/Input";
import { Button } from "@/components/ui/Button";
import { Table, THead, TR, TH, TD } from "@/components/ui/Table";
import { StatusPill } from "@/components/ui/StatusPill";
import { formatCurrency } from "@/lib/format";
import type { Product, ProductVariant } from "@/types/catalog";

interface VariantForm {
  sku: string;
  price: string;
  qualification_point_override: string;
  // attributes removed from here since we'll manage it with state
}

export function VariantFormModal({
  open, onClose, product, onSaved,
}: { open: boolean; onClose: () => void; product: Product; onSaved: () => void }) {
  const [adding, setAdding] = useState(false);
  const [attrKey, setAttrKey] = useState("");
  const [attrVal, setAttrVal] = useState("");
  const [attributes, setAttributes] = useState<Record<string, string>>({});
  
  const qc = useQueryClient();
  const { register, handleSubmit, reset, formState: { errors } } = useForm<VariantForm>();

  // Function to add attribute to the list:
  const handleAddAttr = () => {
    if (!attrKey || !attrVal) return;
    setAttributes(prev => ({ ...prev, [attrKey]: attrVal }));
    setAttrKey(""); 
    setAttrVal("");
  };

  const create = useMutation({
    mutationFn: (d: VariantForm) => {
      // Now we just use the attributes state directly - no JSON parsing needed!
      return post<ProductVariant>(`/admin/products/${product.id}/variants`, {
        sku: d.sku,
        price: d.price,
        attributes: attributes, // Directly pass the state object
        qualification_point_override: d.qualification_point_override
          ? Number(d.qualification_point_override) : null,
      });
    },
    onSuccess: () => {
      toast.success("Variant added");
      reset();
      setAttributes({}); // Clear attributes
      setAdding(false);
      qc.invalidateQueries({ queryKey: ["admin-products"] });
      onSaved();
    },
  });

  const toggle = useMutation({
    mutationFn: (v: ProductVariant) =>
      patch<ProductVariant>(`/admin/variants/${v.id}`, {
        status: v.status === "active" ? "inactive" : "active",
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-products"] });
    },
  });

  return (
    <Modal open={open} onClose={onClose}
           title={`Variants of ${product.name}`} size="xl">
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <div className="text-sm text-surface-600">
            {product.variants.length} variant(s) · Product code {product.product_code}
          </div>
          {!adding && (
            <Button size="sm" leftIcon={<Plus className="h-3.5 w-3.5" />}
                    onClick={() => setAdding(true)}>
              Add variant
            </Button>
          )}
        </div>

        {adding && (
          <form onSubmit={handleSubmit((d) => create.mutate(d))}
                className="border border-brand-200 bg-brand-50/40 rounded-lg p-4 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <Input label="SKU" placeholder={`${product.product_code}-A`}
                     error={errors.sku?.message}
                     {...register("sku", { required: "Required" })} />
              <Input label="Price" type="number" step="0.01"
                     error={errors.price?.message}
                     {...register("price", { required: "Required" })} />
            </div>
            <Input label="Point override (optional)" type="number"
                   hint="Leave empty to inherit product default"
                   {...register("qualification_point_override")} />
            
            {/* New Options Builder UI - replaces the old JSON input */}
            <div className="border border-surface-200 rounded-lg p-3 bg-white">
              <div className="text-xs font-semibold text-surface-500 uppercase mb-2">
                Options (e.g. Color: Red)
              </div>
              <div className="flex flex-wrap gap-2 mb-3">
                {Object.entries(attributes).map(([k, v]) => (
                  <span key={k} className="inline-flex items-center gap-1 bg-brand-50 text-brand-700 px-2 py-1 rounded text-xs font-bold border border-brand-200">
                    {k}: {v}
                    <button type="button" onClick={() => {
                      const next = {...attributes}; 
                      delete next[k]; 
                      setAttributes(next);
                    }}>
                      <Trash2 className="h-3 w-3 hover:text-danger-500" />
                    </button>
                  </span>
                ))}
                {Object.keys(attributes).length === 0 && (
                  <span className="text-surface-400 text-xs italic">No options added yet</span>
                )}
              </div>
              <div className="flex gap-2">
                <Input 
                  placeholder="Option (Color)" 
                  value={attrKey} 
                  onChange={e => setAttrKey(e.target.value)} 
                />
                <Input 
                  placeholder="Value (Red)" 
                  value={attrVal} 
                  onChange={e => setAttrVal(e.target.value)} 
                />
                <Button type="button" variant="secondary" onClick={handleAddAttr}>
                  Add
                </Button>
              </div>
            </div>
            
            <div className="flex justify-end gap-2">
              <Button variant="ghost" type="button" size="sm"
                      onClick={() => { 
                        setAdding(false); 
                        reset();
                        setAttributes({});
                      }}>
                Cancel
              </Button>
              <Button type="submit" size="sm" loading={create.isPending}>Save variant</Button>
            </div>
          </form>
        )}

        {product.variants.length > 0 ? (
          <Table>
            <THead>
              <TR>
                <TH>SKU</TH><TH>Price</TH><TH>Points</TH>
                <TH>Attributes</TH><TH>Status</TH><TH></TH>
              </TR>
            </THead>
            <tbody>
              {product.variants.map((v) => (
                <TR key={v.id}>
                  <TD className="font-mono text-xs">{v.sku}</TD>
                  <TD>{formatCurrency(v.price)}</TD>
                  <TD>
                    {v.qualification_point_override ?? (
                      <span className="text-surface-400 italic text-xs">inherit</span>
                    )}
                  </TD>
                  <TD className="text-xs">
                    {Object.entries(v.attributes).map(([k, val]) => (
                      <span key={k} className="mr-2">
                        <span className="font-semibold">{k}:</span> {val}
                      </span>
                    ))}
                  </TD>
                  <TD><StatusPill status={v.status} /></TD>
                  <TD>
                    <Button size="sm" variant="ghost" onClick={() => toggle.mutate(v)}>
                      {v.status === "active" ? "Deactivate" : "Activate"}
                    </Button>
                  </TD>
                </TR>
              ))}
            </tbody>
          </Table>
        ) : (
          <div className="text-center text-sm text-surface-500 py-6">No variants yet.</div>
        )}
      </div>
    </Modal>
  );
}